import { TransactionInformationItem } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/components/TransactionInformationItem';
import { ExistingFileContainer } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/components/TransactionInformationItem/styled';
import { LinkUploadContainer } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/styled';
import { MatchedDocumentGroup } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/ManageFileAssociationStep.types';
import { DocumentIdentity } from '@dealstack/frontend/shared/components/Documents/types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { MatchedDocumentsRes } from '@dealstack/shared/lib/generated';
import { Button, Flex, Text } from '@mantine/core';
import { useState } from 'react';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	group: MatchedDocumentGroup;
	onViewExistingDocument?: (existingDocument: DocumentIdentity) => void;
}

/**
 * Three states, not two: findDocumentWithRelations LEFT JOINs DocumentLink, so a
 * document with no live link comes back with both ids null. Returning undefined
 * for that case is what makes TransactionInformationItem render "No links".
 */
const resolveLinkedTo = ({
	personId,
	linkedTransactionId,
}: MatchedDocumentsRes): 'person' | 'transaction' | undefined => {
	if (personId) {
		return 'person';
	}

	return linkedTransactionId ? 'transaction' : undefined;
};

export const ExistingFiles = ({ group, onViewExistingDocument }: Props) => {
	const tCapManage = useTranslationCapManage();
	const [isTransactionListVisible, setIsTransactionListVisible] = useState<boolean>(false);

	const toggleShouldHideRelatedTransactions = () => {
		setIsTransactionListVisible((prev) => !prev);
	};

	return (
		<ExistingFileContainer>
			<Icon variant="document-v3-rounded" size="1.5rem" />
			{/* minWidth 0 lets the column shrink instead of setting the card's width —
			    without it a long document name escapes the centred container. */}
			<Flex
				data-testid={makeComplexDataTestId(
					DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_EXISTING_FILE_CARD,
					group.documentName
				)}
				direction="column"
				style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
			>
				{/* The panel's own Header already reads "Existing file found" — repeating it per
				    card just duplicated the same sentence twice on screen. */}
				<Text fw="bold" truncate>
					{group.documentName}
				</Text>

				<LinkUploadContainer>
					{isTransactionListVisible &&
						group.links.map((entity, index) => (
							<TransactionInformationItem
								key={`${entity.transactionId}_${index}`}
								buyerName={entity.buyerName}
								sellerName={entity.sellerName}
								issuerName={entity.issuerName}
								securityName={entity.securityName}
								transactionDate={entity.transactionDate}
								transactionType={entity.transactionType}
								securityUnitDenominator={entity.securityUnitDenominator}
								linkedTo={resolveLinkedTo(entity)}
								securityIssuedIn={entity.securityIssuedIn ?? undefined}
								securityCurrency={entity.securityCurrency ?? undefined}
								units={entity.units}
								price={entity.cost}
								disabled={true}
								checked={true}
								transactionId={entity.transactionId}
							/>
						))}
				</LinkUploadContainer>

				{group.links.length > 0 && (
					<Button
						variant="white"
						onClick={toggleShouldHideRelatedTransactions}
						styles={{
							inner: { justifyContent: 'flex-start' },
							root: {
								padding: '0px!important',
							},
						}}
						fullWidth
					>
						{isTransactionListVisible
							? tCapManage('document.link_duplicate_files.right_side_bar.button.hide')
							: tCapManage(
									'document.link_duplicate_files.right_side_bar.button.show'
								)}
					</Button>
				)}

				{onViewExistingDocument && (
					<Button
						data-testid={makeComplexDataTestId(
							DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_EXISTING_FILE_VIEW_DOCUMENT,
							group.documentName
						)}
						variant="white"
						leftSection={<Icon variant="arrow-right-v3" size="1rem" />}
						onClick={() =>
							onViewExistingDocument({
								documentId: group.documentId,
								fileId: group.fileId,
								documentName: group.documentName,
							})
						}
						styles={{
							inner: { justifyContent: 'flex-start' },
							root: {
								padding: '0px!important',
							},
						}}
						fullWidth
					>
						{tCapManage(
							'document.link_duplicate_files.right_side_bar.already.view_document'
						)}
					</Button>
				)}
			</Flex>
		</ExistingFileContainer>
	);
};
